import React from 'react';
import { Box, Typography, Paper, Button, Grid, Fade, Grow } from '@mui/material';
import { Link } from 'react-router-dom';
import PersonIcon from '@mui/icons-material/Person';
import SchoolIcon from '@mui/icons-material/School';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { styled, keyframes } from '@mui/material/styles';

interface InitialChoiceProps {
  onPersonalClick: () => void;
  onSchoolClick: () => void;
}

// Floating animation (same as InitialLogin)
const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-6px); }
  100% { transform: translateY(0px); }
`;

const ContentContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: 800,
  margin: '0 auto',
  position: 'relative',
  
  // Subtle background for the content area
  '&::before': {
    content: '""',
    position: 'absolute',
    top: -20,
    left: -20,
    right: -20,
    bottom: -20,
    background: `
      radial-gradient(circle at 30% 70%, rgba(42, 146, 53, 0.03) 0%, transparent 50%),
      radial-gradient(circle at 70% 30%, rgba(76, 175, 80, 0.03) 0%, transparent 50%)
    `,
    borderRadius: 24,
    zIndex: -1,
  }
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(6),
}));

const MainTitle = styled(Typography)(({ theme }) => ({
  // match the provided snippet: responsive sizes, weight, gradient text and fallback
  fontSize: '1.75rem',
  fontWeight: 700,
  marginBottom: theme.spacing(1.5),
  letterSpacing: '-0.02em',
  position: 'relative',
  background: 'linear-gradient(135deg, #2A9235 0%, #4CAF50 100%)',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  color: 'transparent',
  // provide a readable fallback for browsers that don't support background-clip:text
  WebkitTextFillColor: theme.palette.text.secondary,

  [theme.breakpoints.up('md')]: {
    fontSize: '2.25rem',
  },

  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -8,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 80,
    height: 3,
    background: 'linear-gradient(90deg, #2A9235, #4CAF50)',
    borderRadius: 2,
  }
}));

const SubTitle = styled(Typography)(({ theme }) => ({
  // tighter, consistent sizing and placement under the main title
  fontSize: '1rem',
  fontWeight: 400,
  color: theme.palette.text.secondary,
  lineHeight: 1.6,
  marginTop: theme.spacing(0.5),
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up('md')]: {
    fontSize: '1.125rem',
  }
}));

const ChoiceCard = styled(Paper)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(4, 3),
  height: '100%',
  minHeight: 420,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  borderRadius: 20,
  border: '1px solid rgba(42, 146, 53, 0.1)',
  background: `
    linear-gradient(145deg, 
      rgba(255, 255, 255, 0.95) 0%, 
      rgba(248, 250, 252, 0.95) 100%
    )
  `,
  backdropFilter: 'blur(10px)',
  overflow: 'hidden',
  transition: 'all 0.5s cubic-bezier(0.23, 1, 0.32, 1)',
  boxShadow: `
    0 6px 24px rgba(42, 146, 53, 0.08),
    0 3px 12px rgba(0, 0, 0, 0.04)
  `,
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(42, 146, 53, 0.05), transparent)',
    transition: 'left 0.6s ease',
  },
  
  '&:hover': {
    transform: 'translateY(-12px) scale(1.02)',
    borderColor: 'rgba(42, 146, 53, 0.3)',
    boxShadow: `
      0 16px 48px rgba(42, 146, 53, 0.15),
      0 8px 24px rgba(0, 0, 0, 0.08)
    `,
    background: `
      linear-gradient(145deg, 
        rgba(255, 255, 255, 0.98) 0%, 
        rgba(240, 249, 242, 0.95) 100%
      )
    `,
    
    '&::before': {
      left: '100%',
    },
    
    '& .card-icon-wrapper': {
      transform: 'scale(1.1)',
      background: 'linear-gradient(135deg, #2A9235 0%, #4CAF50 100%)',
      '& .card-icon': {
        color: 'white',
        transform: 'scale(1.1)',
      }
    },
    
    '& .card-button': {
      background: 'linear-gradient(135deg, #2A9235 0%, #4CAF50 100%)',
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 24px rgba(42, 146, 53, 0.3)',
      
      '& .button-arrow': {
        transform: 'translateX(6px)',
      }
    }
  }
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  width: 80,
  height: 80,
  borderRadius: '50%',
  background: 'linear-gradient(135deg, rgba(42, 146, 53, 0.1) 0%, rgba(76, 175, 80, 0.05) 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(3),
  transition: 'all 0.5s cubic-bezier(0.23, 1, 0.32, 1)',
  border: '2px solid rgba(42, 146, 53, 0.1)',
  animation: `${float} 6s ease-in-out infinite`,
}));

const CardTitle = styled(Typography)(({ theme }) => ({
  fontSize: 'clamp(1.3rem, 2.5vw, 1.6rem)',
  fontWeight: 700,
  color: '#1a1a1a',
  marginBottom: theme.spacing(2),
  letterSpacing: '-0.01em',
}));

const CardDescription = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  lineHeight: 1.6,
  fontSize: '1rem',
  marginBottom: theme.spacing(4),
  flexGrow: 1,
  display: 'flex',
  alignItems: 'center',
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 14,
  padding: theme.spacing(1.3, 3),
  fontSize: '1rem',
  fontWeight: 600,
  textTransform: 'none',
  background: 'linear-gradient(135deg, rgba(42, 146, 53, 0.9) 0%, rgba(76, 175, 80, 0.9) 100%)',
  color: 'white',
  border: 'none',
  width: '100%',
  minHeight: 48,
  transition: 'all 0.4s cubic-bezier(0.23, 1, 0.32, 1)',
  boxShadow: '0 6px 20px rgba(42, 146, 53, 0.2)',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
    transition: 'left 0.5s ease',
  },
  
  '&:hover::before': {
    left: '100%',
  }
}));

const HelpSection = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(3),
  borderRadius: 16,
  background: `
    linear-gradient(135deg, 
      rgba(255, 255, 255, 0.8) 0%, 
      rgba(248, 250, 252, 0.9) 100%
    )
  `,
  backdropFilter: 'blur(8px)',
  border: '1px solid rgba(42, 146, 53, 0.1)',
  margin: '0 auto',
  marginTop: theme.spacing(5),
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)',
}));

const LoginLink = styled(Typography)(({ theme }) => ({
  color: '#2A9235',
  fontSize: '1.125rem',
  fontWeight: 800,
  cursor: 'pointer',
  position: 'relative',
  display: 'inline-block',
  transition: 'all 0.3s ease',
  textDecoration: 'none',
  
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -1,
    left: 0,
    width: 0,
    height: 2,
    background: '#2A9235',
    transition: 'width 0.3s ease',
  },
  
  '&:hover': {
    color: '#1e7a2b',
    transform: 'translateY(-1px)',
    
    '&::after': {
      width: '100%',
    }
  }
}));

const InitialChoice: React.FC<InitialChoiceProps> = ({ onPersonalClick, onSchoolClick }) => {
  return (
    <ContentContainer>
      <Fade in={true} timeout={800}>
        <HeaderSection>
          <MainTitle variant="h2" >
            Welcome! Let's get you set up
          </MainTitle>
          <SubTitle variant="h5">
            Choose the option that best fits your needs
          </SubTitle>
        </HeaderSection>
      </Fade>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6}>
          <Grow in={true} timeout={1000}>
            <ChoiceCard elevation={0}>
              <IconWrapper className="card-icon-wrapper">
                <PersonIcon 
                  className="card-icon"
                  sx={{ 
                    fontSize: 40, 
                    color: '#2A9235',
                    transition: 'all 0.4s ease'
                  }} 
                />
              </IconWrapper>
              
              <CardTitle variant="h3">
                Personal & Family
              </CardTitle>
              
              <CardDescription variant="body1">
                Perfect for individual learners or parents guiding their child's spelling journey at home
              </CardDescription>
              
              <ActionButton 
                className="card-button"
                onClick={onPersonalClick}
                variant="contained" 
                size="large"
                endIcon={
                  <ArrowForwardIcon 
                    className="button-arrow"
                    sx={{ 
                      transition: 'transform 0.3s ease',
                      fontSize: 18 
                    }} 
                  />
                }
              >
                Start My Individual Plan
              </ActionButton>
            </ChoiceCard>
          </Grow>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Grow in={true} timeout={1200}>
            <ChoiceCard elevation={0}>
              <IconWrapper className="card-icon-wrapper">
                <SchoolIcon 
                  className="card-icon"
                  sx={{ 
                    fontSize: 40, 
                    color: '#2A9235',
                    transition: 'all 0.4s ease'
                  }} 
                />
              </IconWrapper>
              
              <CardTitle variant="h3">
                School & Classroom
              </CardTitle>
              
              <CardDescription variant="body1">
                Designed for teachers, administrators, and districts managing multiple student accounts
              </CardDescription>
              
              <ActionButton 
                className="card-button"
                onClick={onSchoolClick}
                variant="contained" 
                size="large"
                endIcon={
                  <ArrowForwardIcon 
                    className="button-arrow"
                    sx={{ 
                      transition: 'transform 0.3s ease',
                      fontSize: 18 
                    }} 
                  />
                }
              >
                Set Up My School/Classroom
              </ActionButton>
            </ChoiceCard>
          </Grow>
        </Grid>
      </Grid>

      <Fade in={true} timeout={1400}>
        <HelpSection>
          <Typography 
            variant="body1" 
            sx={{ 
              fontSize: '1.125rem',
              color: 'text.secondary',
              lineHeight: 1.6
            }}
          >
            Already have an account?{' '}
            <Link 
              to="/login" 
              style={{ textDecoration: 'none' }}
            >
              <LoginLink >
                Log In
              </LoginLink>
            </Link>
          </Typography>
        </HelpSection>
      </Fade>
    </ContentContainer>
  );
};

export default InitialChoice;
